import { useEffect, useRef, useState } from 'react';
import { zohoApi, type ZohoCustomer } from '../../services/api';

/**
 * Typeahead pour sélectionner un customer Zoho Books. Recherche serveur
 * débouncée (filtre Zoho contact_name_contains / company_name_contains).
 * Affiche les 10 premiers résultats sous l'input.
 *
 * Valeur contrôlée : { id, name }. Le parent décide quoi stocker en form.
 */
interface ClientPickerProps {
  value: { id: string | null; name: string | null };
  onChange: (v: { id: string; name: string }) => void;
  onClear?: () => void;
  required?: boolean;
  placeholder?: string;
}

export function ClientPicker({
  value,
  onChange,
  onClear,
  required,
  placeholder = 'Tape pour chercher…',
}: ClientPickerProps) {
  const [query, setQuery] = useState(value.name ?? '');
  const [results, setResults] = useState<ZohoCustomer[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local query avec la valeur du parent quand celle-ci change de
  // l'extérieur (ex. duplication d'une autre ligne, reset, etc.).
  useEffect(() => {
    setQuery(value.name ?? '');
  }, [value.name, value.id]);

  // Debounce 250 ms sur la recherche serveur.
  useEffect(() => {
    if (!open) return;
    if (timer.current) window.clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await zohoApi.searchCustomers(query.trim());
        setResults(resp.customers);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [query, open]);

  // Fermer la dropdown au clic en-dehors.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, []);

  const select = (c: ZohoCustomer) => {
    setQuery(c.name);
    setResults([]);
    setOpen(false);
    onChange({ id: c.id, name: c.name });
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Si l'utilisateur édite après sélection, on considère que la
          // valeur courante n'est plus valide tant qu'il ne re-clique pas
          // sur un résultat.
          if (value.id && e.target.value !== value.name) {
            onClear?.();
          }
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        required={required}
        className={`w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          required && !value.id ? 'border-red-300 bg-red-50' : 'border-slate-300'
        }`}
      />
      {open && (query.trim().length >= 2 || results.length > 0) && (
        <div className="absolute z-10 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white rounded-md border border-slate-200 shadow-lg text-sm">
          {loading && (
            <div className="px-3 py-2 text-slate-500 italic">Recherche…</div>
          )}
          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="px-3 py-2 text-slate-500 italic">Aucun résultat</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => select(c)}
              className="block w-full text-left px-3 py-2 hover:bg-slate-100"
            >
              <div className="font-medium">{c.name}</div>
              {c.company && c.company !== c.name && (
                <div className="text-xs text-slate-500">{c.company}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
